import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import { Avatar, Box, Button, Collapse, IconButton, Link, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip, Typography, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as send from '../../../../../lib/analytics/send';
import { dataConstants } from '../../../../constants';
import { roundNum } from '../../../../helpers';
import { convertDate } from '../../../../helpers/time';
import { needPremium } from '../../../../needPremium';
import { addCommas } from '../../../../utils';
import { downloadCSV } from '../../../../utils/helpers';
import StockHoldingsChart from '../../../_common/VisxChart/StockHoldingsChart';

const { FUNDS_SECTIONS, SORT_DIR } = dataConstants || {};

const DEFAULT_SORT_FIELD = 'PctPortfolio';

const C = {
  body: '#33373f',
  muted: '#52586a',
  mutedStrong: '#4a5060',
  hair: '#ececf0',
  headRule: '#d9dbe2',
  link: '#0b4ea2',
  up: '#0a5d20',
  down: '#a01b13',
  newPos: '#0b4ea2',
  selectWash: '#fafbfc',
  hoverWash: '#f6f7f9',
  barTrack: '#e7e9ef',
  barFill: '#6b7280',
  logoBg: '#f2f3f6',
};

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border: theme.palette.mode === 'dark' ? '1px solid rgb(19, 47, 76)' : '1px solid #eae9ee',
  borderRadius: '7px',
  '& .col-md-4:hover button, .share-wrapper': { visibility: 'visible' },
  '& .Item-Head': {
    background: '#fbfbfc',
    marginBottom: 0,
    padding: theme.spacing(1.5, 2, 1.2, 1.5),
    borderRadius: '7px 7px 0 0',
    '& .option-title': { fontWeight: 600, color: '#13113c', fontSize: '1rem' }
  },
  '& .section': {
    '& .option-title': {
      borderTop: '1px solid rgba(224, 224, 224, 1)',
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
      fontSize: '1rem',
      paddingTop: theme.spacing(1),
      paddingBottom: 0,
      paddingLeft: theme.spacing(2),
    },
  },
}));

const getChart = (chart, code, data, cik) => ({
  ...chart,
  series: [{ ...chart?.series?.[0], yformula: code.replace('XXXXX', cik) }],
  ySeries: code,
  series_data: data,
});

const toTitle = (s) =>
  typeof s === 'string' ? s.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase()) : s;

const fmtMoneyCompact = (n) => {
  const v = Number(n);
  if (isNaN(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${addCommas(abs)}`;
};

const compareRows = (a, b, field, dir) => {
  const av = a?.[field];
  const bv = b?.[field];
  const aNull = av == null || av === '';
  const bNull = bv == null || bv === '';
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  const an = Number(av);
  const bn = Number(bv);
  const cmp = (!isNaN(an) && !isNaN(bn))
    ? an - bn
    : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
  return dir === SORT_DIR.ASC ? cmp : -cmp;
};

const HoldingLogo = ({ ticker }) => {
  const [errored, setErrored] = useState(false);
  const src = ticker && !errored ? `https://logo.grufity.com/${ticker}.png` : undefined;
  return (
    <Avatar
      src={src}
      alt=""
      aria-hidden="true"
      variant="rounded"
      imgProps={{ loading: 'lazy', onError: () => setErrored(true) }}
      sx={{ width: 36, height: 36, bgcolor: '#fff', color: C.muted, fontSize: '.7rem', flexShrink: 0, border: `1p solid ${C.hair}`, '& img': { objectFit: 'contain', borderRadius: "4px" }, '&:hover .MuiSvgIcon-root': { color: '#174ea63a' } }}
    >
      <ApartmentRoundedIcon sx={{ width: 37, height: 37, color: C.logoBg, }} />
    </Avatar>
  );
};

const Delta = ({ pct, changeType }) => {
  const isNew = changeType && /new/i.test(String(changeType));
  if (pct == null || isNaN(pct)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px' }}>
        <Box component="span" sx={{ color: isNew ? C.newPos : C.mutedStrong, fontWeight: 600 }}>
          <Box component="span" aria-hidden="true">{isNew ? 'New' : '—'}</Box>
          <Box component="span" sx={visuallyHidden}>{isNew ? 'new position' : 'no change data'}</Box>
        </Box>
        {changeType && !isNew && (
          <Box component="span" sx={{ fontSize: '.62rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: C.muted }}>
            {String(changeType).toLowerCase()}
          </Box>
        )}
      </Box>
    );
  }
  const n = Number(pct);
  const color = n > 0 ? C.up : n < 0 ? C.down : C.mutedStrong;
  const Icon = n > 0 ? ArrowDropUpRoundedIcon : n < 0 ? ArrowDropDownRoundedIcon : null;
  const word = n > 0 ? 'increased' : n < 0 ? 'decreased' : 'unchanged';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0 }}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {Icon && <Icon aria-hidden="true" sx={{ fontSize: '1rem', ml: '-3px', mr: '-1px' }} />}
        <Box component="span" aria-hidden="true">{Math.abs(n)}%</Box>
        <Box component="span" sx={visuallyHidden}>{word} {Math.abs(n)} percent</Box>
      </Box>
      {changeType && (
        <Box component="span" sx={{ fontSize: '.62rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: isNew ? C.newPos : C.muted }}>
          {String(changeType).toLowerCase()}
        </Box>
      )}
    </Box>
  );
};

const ChartHeader = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1, px: 0.5 }}>
    <Typography component="span" sx={{ fontSize: '.7rem', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: C.muted, lineHeight: 1.3 }}>
      {label}
    </Typography>
    {value != null && (
      <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>
        {value}
      </Typography>
    )}
  </Box>
);

const CurrentHoldingsTable = ({ data, sorting, setSorting, requestChartData, setChartInit, themeMode, themes, name, tickers, selHoldTicker, symbolCodes, stockTickers, isPremium, showPricingBanner, cik, txtBlock }) => {
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const rowsPerPage = isMobile ? 10 : 20;

  const holdings = useMemo(
    () => (Array.isArray(data) ? data.find(d => d.section_code === FUNDS_SECTIONS.FUND_HOLDINGS) : undefined),
    [data]
  );

  const holdingCols = holdings?.settings?.cols;

  const activeSort = sorting?.[FUNDS_SECTIONS.FUND_HOLDINGS] || {};
  const sortField = activeSort.colN && holdingCols?.[activeSort.colN - 1]
    ? holdingCols[activeSort.colN - 1].field
    : DEFAULT_SORT_FIELD;
  const sortDir = activeSort.dir || SORT_DIR.DESC;

  const fundShr = useMemo(() => (Array.isArray(data) ? data.find(d => d.section_code === FUNDS_SECTIONS.FUND_SHR) : undefined), [data]);
  const fundShrChg = useMemo(() => (Array.isArray(data) ? data.find(d => d.section_code === FUNDS_SECTIONS.FUND_SHR_CHG) : undefined), [data]);
  const fundPort = useMemo(() => (Array.isArray(data) ? data.find(d => d.section_code === FUNDS_SECTIONS.FUND_PORT_PCT) : undefined), [data]);

  const holdingsTbl = useMemo(() => {
    const raw = holdings?.tabData;
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    const filtered = raw.filter(t => t && t.putCall !== 'PUT' && t.putCall !== 'CALL');
    if (filtered.length === 0) return undefined;
    const deduped = filtered.filter((v, i, a) => a.findIndex(t => JSON.stringify(t) === JSON.stringify(v)) === i);
    return [...deduped].sort((a, b) => compareRows(a, b, sortField, sortDir));
  }, [holdings, sortField, sortDir]);

  const fundShrC = useMemo(
    () => (fundShr?.tabData && symbolCodes && symbolCodes.length > 0
      ? getChart(fundShr.tabData, symbolCodes[0], (selHoldTicker && tickers?.[selHoldTicker]?.[symbolCodes[0]]) || fundShr.tabData.series_data, cik)
      : undefined),
    [fundShr, symbolCodes, selHoldTicker, tickers, cik]
  );
  const fundShrChgC = useMemo(
    () => (fundShrChg?.tabData && symbolCodes && symbolCodes.length > 1
      ? getChart(fundShrChg.tabData, symbolCodes[1], (selHoldTicker && tickers?.[selHoldTicker]?.[symbolCodes[1]]) || fundShrChg.tabData.series_data, cik)
      : undefined),
    [fundShrChg, symbolCodes, selHoldTicker, tickers, cik]
  );
  const fundPortC = useMemo(
    () => (fundPort?.tabData && symbolCodes && symbolCodes.length > 2
      ? getChart(fundPort.tabData, symbolCodes[2], (selHoldTicker && tickers?.[selHoldTicker]?.[symbolCodes[2]]) || fundPort.tabData.series_data, cik)
      : undefined),
    [fundPort, symbolCodes, selHoldTicker, tickers, cik]
  );

  const description = useMemo(() => {
    let parsed;
    try { parsed = JSON.parse(txtBlock); } catch (e) { return null; }
    return Array.isArray(parsed) && parsed.length > 0 ? parsed.find(d => d.section === 'current_holdings') : null;
  }, [txtBlock]);

  const handleDownload = useCallback((e) => {
    if (isPremium) {
      if (!holdingsTbl || holdingsTbl.length === 0) return;
      downloadCSV(
        holdingsTbl,
        holdingCols,
        `Current Holdings of ${holdings?.section_name?.replace('XXXXX', name) ?? name} (Last Reported on: ${convertDate(holdingsTbl[0].dateFiled, 'dd MMM, yyyy')})`,
        true
      );
      send.uevent(e);
    } else if (typeof showPricingBanner === 'function') {
      showPricingBanner(e, 'currholds');
    }
  }, [isPremium, holdingsTbl, holdingCols, holdings, name, showPricingBanner, cik]);

  const handleChangePage = useCallback((event, newPage) => setPage(newPage), []);

  useEffect(() => {
    setPage(1);
    setOpenRow(null);
  }, [cik]);

  const latestVal = useCallback((section, isPct) => {
    const arr = section?.tabData?.series_data;
    if (!Array.isArray(arr) || arr.length === 0) return null;

    const latest = arr.reduce((maxObj, currentObj) => {
      const maxDate = maxObj?.recordDate ? new Date(maxObj.recordDate).getTime() : 0;
      const currDate = currentObj?.recordDate ? new Date(currentObj.recordDate).getTime() : 0;
      return currDate > maxDate ? currentObj : maxObj;
    }, arr[0]);

    const v = latest?.value;
    if (v == null || isNaN(v)) return null;
    return isPct ? `${roundNum(v)}%` : addCommas(v);
  }, []);

  const maxPortfolio = useMemo(
    () => (holdingsTbl ? holdingsTbl.reduce((m, r) => Math.max(m, Number(r.PctPortfolio) || 0), 0) || 1 : 1),
    [holdingsTbl]
  );

  const isLeftCol = (f) => f === 'ticker' || f === 'nameOfIssuer';
  const isHiddenCol = (f) => f === 'nameOfIssuer' || f === 'changeType' || f === 'putCall';

  const handleSort = (col, realIndex) => {
    const isActive = sortField === col.field;
    const naturalDefault = isLeftCol(col.field) ? SORT_DIR.ASC : SORT_DIR.DESC;
    const nextDir = isActive
      ? (sortDir === SORT_DIR.ASC ? SORT_DIR.DESC : SORT_DIR.ASC)
      : naturalDefault;
    setSorting(FUNDS_SECTIONS.FUND_HOLDINGS, { colN: realIndex + 1, dir: nextDir });
    setPage(1);
  };

  const renderCell = (c, d, hasLink, isExpanded, fundName) => {
    const field = c.field;
    const raw = d[field];

    if (field === 'nameOfIssuer') return null;

    if (field === 'ticker') {
      const safeTicker = d.ticker || '';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: { xs: 0, md: 0.1 } }}>
          <HoldingLogo ticker={safeTicker} />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', alignItems: 'center', minWidth: '280px' }}>
            <Box
              sx={{
                gridArea: '1 / 1 / 2 / 2',
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                opacity: isExpanded ? 0 : 1,
                transform: isExpanded ? 'translateY(-2px)' : 'translateY(0)',
                visibility: isExpanded ? 'hidden' : 'visible',
                transition: 'opacity 0.25s ease, transform 0.25s ease, visibility 0.25s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {safeTicker ? (
                  hasLink ? (
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${dataConstants.NAVS.STOCK}/${safeTicker}`}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`${safeTicker}, open stock page in new tab`}
                      sx={{ color: C.link, fontWeight: 700, fontSize: '.85rem', textDecoration: 'none', letterSpacing: '.01em', borderBottom: '1px solid transparent', '&:hover, &:focus-visible': { borderBottomColor: C.link } }}
                    >
                      {safeTicker}
                    </Link>
                  ) : (
                    <Box component="span" sx={{ color: C.ink, fontWeight: 700, fontSize: '.85rem' }}>{safeTicker}</Box>
                  )
                ) : (
                  <Box component="span" sx={{ color: C.muted, fontWeight: 700, fontSize: '.64rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    {d.titleOfClass ? toTitle(d.titleOfClass) : '—'}
                  </Box>
                )}
              </Box>
              <Typography
                component="span"
                sx={{
                  color: C.body,
                  fontSize: '.74rem',
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: '280px',
                  maxWidth: '300px'
                }}
              >
                {toTitle(d.nameOfIssuer)}
              </Typography>
            </Box>

            <Box
              sx={{
                gridArea: '1 / 1 / 2 / 2',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                opacity: isExpanded ? 1 : 0,
                transform: isExpanded ? 'translateY(0)' : 'translateY(2px)',
                visibility: isExpanded ? 'visible' : 'hidden',
                transition: 'opacity 0.25s ease, transform 0.25s ease, visibility 0.25s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'nowrap', gap: 1 }}>
                {hasLink ? (
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${dataConstants.NAVS.STOCK}/${safeTicker}`}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`${toTitle(d.nameOfIssuer)}, open stock page in new tab`}
                    sx={{ fontSize: '.875rem', fontWeight: 700, color: C.link, textDecoration: 'none', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', '&:hover, &:focus-visible': { textDecoration: 'underline' } }}
                  >
                    {toTitle(d.nameOfIssuer?.replace('YYYYY', safeTicker))}
                  </Link>
                ) : (
                  <Typography variant="h3" component="h3" sx={{ fontSize: '.875rem', fontWeight: 700, color: C.ink, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {toTitle(d.nameOfIssuer?.replace('YYYYY', safeTicker))}
                  </Typography>
                )}
                {safeTicker && (
                  <Box component="span" sx={{ fontSize: '.72rem', fontWeight: 700, letterSpacing: '.03em', color: C.link, bgcolor: 'rgba(11,78,162,0.10)', px: 0.75, py: 0.25, borderRadius: '4px', lineHeight: 1.4, flexShrink: 0 }}>
                    {safeTicker}
                  </Box>
                )}
              </Box>
              <Typography
                component="span"
                sx={{
                  color: C.mutedStrong,
                  fontSize: '.78rem',
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: '280px',
                  maxWidth: '400px'
                }}
              >
                {fundName}'s holding in {toTitle(d.nameOfIssuer)} over time
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    if (field === 'value') {
      const pct = Number(d.PctPortfolio);
      const widthPct = isNaN(pct) ? 0 : Math.max(2, (pct / maxPortfolio) * 100);
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
          <Tooltip title={`$${addCommas(raw)}`} arrow placement="top" enterDelay={400}>
            <Box component="span" sx={{ color: C.ink, fontWeight: 600, fontSize: '.85rem', fontVariantNumeric: 'tabular-nums', cursor: 'help' }}>
              {fmtMoneyCompact(raw)}
            </Box>
          </Tooltip>
          {!isNaN(pct) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
              <Box component="span" sx={{ color: C.mutedStrong, fontSize: '.68rem', fontVariantNumeric: 'tabular-nums' }}>{pct}%</Box>
              <Box aria-hidden="true" sx={{ width: { xs: 30, md: 40 }, height: 4, borderRadius: 2, bgcolor: C.barTrack, overflow: 'hidden', flexShrink: 0 }}>
                <Box sx={{ width: `${widthPct}%`, height: '100%', borderRadius: 2, bgcolor: C.barFill }} />
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    if (field === 'PctPortfolio') {
      return <Box component="span" sx={{ color: C.body, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{isNaN(raw) ? '—' : `${raw}%`}</Box>;
    }

    if (field === 'sshPrnAmt') {
      return <Box component="span" sx={{ color: C.body, fontVariantNumeric: 'tabular-nums' }}>{isNaN(raw) ? toTitle(raw) : addCommas(raw)}</Box>;
    }

    if (field === 'incSh') {
      if (raw == null || isNaN(raw)) return <Box component="span" sx={{ color: C.mutedStrong }} aria-label="no data">—</Box>;
      const n = Number(raw);
      const color = n > 0 ? C.up : n < 0 ? C.down : C.mutedStrong;
      return <Box component="span" sx={{ color, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{n > 0 ? '+' : ''}{addCommas(raw)}</Box>;
    }

    if (field === 'incPct') {
      return <Delta pct={raw} changeType={d.changeType} />;
    }

    if (field === 'incValPct') return <Delta pct={raw} />;

    return (
      <Box component="span" sx={{ color: C.body, fontVariantNumeric: 'tabular-nums' }}>
        {isNaN(raw) ? toTitle(raw) : addCommas(c.float ? roundNum(raw) : raw)}
      </Box>
    );
  };

  if (!holdingsTbl || holdingsTbl.length === 0) return null;

  const pageCount = Math.ceil(holdingsTbl.length / rowsPerPage);
  const safePage = Math.min(page, pageCount) || 1;
  const pagedRows = holdingsTbl.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);

  const visibleCols = holdingCols ? holdingCols.filter(c => !isHiddenCol(c.field)) : [];
  const colSpan = visibleCols.length + 1;

  const totalValue = holdingsTbl.reduce((s, r) => s + (Number(r.value) || 0), 0);
  const positionsCount = holdingsTbl.length;
  const lastDate = holdingsTbl[0]?.dateFiled;

  return (
    <Box component="div" className="col-md-12">
      <Item>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Box className="Item-Head" sx={{ display: 'flex', flexDirection: { md: 'row', sm: 'column', xs: 'column' }, alignItems: 'start', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
              <Box>
                <Typography variant="h2" className="option-title">
                  {holdings?.section_name?.replace('XXXXX', name)}
                </Typography>
                <Typography variant="body2" component="p" sx={{ fontSize: '.9rem', mr: 1, my: 1, color: C.body }} dangerouslySetInnerHTML={{ __html: description && description.text ? description.text : '' }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2 }, mt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography component="span" sx={{ fontSize: '.95rem', fontWeight: 700, color: C.ink }}>{fmtMoneyCompact(totalValue)}</Typography>
                    <Typography component="span" sx={{ fontSize: '.7rem', color: C.mutedStrong, textTransform: 'uppercase', }}>Total Value</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography component="span" sx={{ fontSize: '.95rem', fontWeight: 700, color: C.ink }}>{addCommas(positionsCount)}</Typography>
                    <Typography component="span" sx={{ fontSize: '.7rem', color: C.mutedStrong, textTransform: 'uppercase', }}>Positions</Typography>
                  </Box>
                  {lastDate &&
                    <Box sx={{ display: 'flex', alignItems: 'baseline', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography component="span" sx={{ fontSize: '.95rem', fontWeight: 700, color: C.ink }}>{convertDate(lastDate, 'dd MMM, yyyy')}</Typography>
                      <Typography component="span" sx={{ fontSize: '.7rem', color: C.mutedStrong, textTransform: 'uppercase', }}>Last Reported</Typography>
                    </Box>}
                </Box>
              </Box>
              <Box>
                <Button variant="outlined" size="small"
                  data-event-name="download-csv" data-event-category="fund" data-event-label={cik}
                  startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '.8rem !important' }} aria-hidden="true" />}
                  sx={{
                    px: 1, py: 0.5, lineHeight: 1, color: '#3b4252',
                    background: '#fbfbfc !important', border: '1px solid #c7c9d2',
                    '&:hover': { background: 'transparent !important' },
                    mr: { md: 0, sm: 2, xs: 2 }, mt: { md: 0, sm: 1, xs: 1 }
                  }}
                  onClick={handleDownload}
                  aria-label="Download current holdings as CSV"
                >
                  CSV
                </Button>
              </Box>
            </Box>
            <Box component="div" className="option-content f-hold-tbl">
              <TableContainer sx={{
                maxHeight: '40re', borderTop: `1px solid ${C.hair}`,
                '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '1px' },
                '&:hover::-webkit-scrollbar-thumb': { background: '#020e4a85', borderRadius: '1px' },
                '&::-webkit-scrollbar-track': { background: '#f2f2f2', borderRadius: '1px' },
                '&::-webkit-scrollbar': { width: '5px !important', height: '5px !important' }
              }}>
                <Table stickyHeader size="small" className="sort-tbl"
                  sx={{
                    fontFamily: FONT,
                    '& caption': { ...visuallyHidden },
                    '& .MuiTableCell-head': {
                      fontSize: '.66rem', fontWeight: 700, letterSpacing: '.06em',
                      textTransform: 'uppercase', color: `${C.muted} !important`,
                      padding: { xs: '6px 8px', md: '8px 12px' },
                      borderBottom: `1px solid ${C.hair}`, background: '#fff',
                      whiteSpace: 'nowrap',
                    },
                    '& .MuiTableCell-body': {
                      fontSize: '.84rem', padding: { xs: '4px 8px', md: '6px 12px' },
                      borderBottom: `1px solid ${C.hair}`, verticalAlign: 'middle',
                    },
                    '& .MuiTableRow-root': { transition: 'background-color .12s ease' },
                    '& .MuiTableRow-root:hover .MuiTableCell-body': { bgcolor: C.hoverWash },
                    '& .MuiTableSortLabel-root': { color: C.muted },
                    '& .MuiTableSortLabel-root:hover': { color: C.ink },
                    '& .MuiTableSortLabel-root.Mui-active': { color: `${C.ink} !important` },
                    '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': { color: `${C.ink} !important`, opacity: 1 },
                    '& .MuiTableSortLabel-icon': { opacity: 0.4, fontSize: '1rem' },
                  }}
                >
                  <caption>
                    Current holdings of {holdings?.section_name?.replace('XXXXX', name)}
                    {lastDate ? `, last reported on ${convertDate(lastDate, 'dd MMM, yyyy')}` : ''}.
                    Select a row with a linked ticker to view its historical trend.
                  </caption>
                  <TableHead>
                    <TableRow>
                      <TableCell scope="col" sx={{ width: { xs: 30, md: 40 }, background: '#fff !important', px: { xs: '4px !important', md: '12px !important' } }}>
                        <Box component="span" sx={visuallyHidden}>Expand</Box>
                      </TableCell>
                      {visibleCols.map((c) => {
                        const realIndex = holdingCols.indexOf(c);
                        const isActive = sortField === c.field;
                        return (
                          <TableCell
                            key={c.field}
                            scope="col"
                            aria-sort={isActive ? (sortDir === SORT_DIR.DESC ? 'descending' : 'ascending') : 'none'}
                            sx={{ textAlign: isLeftCol(c.field) ? 'left' : 'right' }}
                          >
                            <TableSortLabel
                              active={isActive}
                              direction={isActive ? (sortDir === SORT_DIR.DESC ? 'desc' : 'asc') : 'asc'}
                              onClick={() => handleSort(c, realIndex)}
                              sx={{ flexDirection: isLeftCol(c.field) ? 'row' : 'row-reverse' }}
                            >
                              {c.field === 'ticker' ? 'Holding' : c.name}
                              {isActive ? (
                                <Box component="span" sx={visuallyHidden}>
                                  {sortDir === SORT_DIR.DESC ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                              ) : null}
                            </TableSortLabel>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRows.map((d, i) => {
                      const hasLink = Array.isArray(stockTickers) && stockTickers.length > 0 ? stockTickers.find(e => e.t === d.ticker) : null;
                      const isSelected = !!selHoldTicker && selHoldTicker === d?.ticker;
                      const isExpanded = openRow === i && isSelected;
                      const rowKey = `${d?.ticker ?? 'row'}-${(safePage - 1) * rowsPerPage + i}`;
                      const rowLabel = `${d.ticker || toTitle(d.nameOfIssuer)}${hasLink ? ', show historical trend' : ''}`;
                      return (
                        <React.Fragment key={rowKey}>
                          <TableRow
                            hover
                            role={hasLink ? 'button' : undefined}
                            tabIndex={hasLink ? 0 : -1}
                            aria-expanded={hasLink ? isExpanded : undefined}
                            aria-label={hasLink ? rowLabel : undefined}
                            onClick={() => {
                              if (d?.ticker && hasLink) {
                                requestChartData(d.ticker);
                                setOpenRow(prev => (prev === i ? null : i));
                              }
                            }}
                            onKeyDown={(e) => {
                              if ((e.key === 'Enter' || e.key === ' ') && d?.ticker && hasLink) {
                                e.preventDefault();
                                requestChartData(d.ticker);
                                setOpenRow(prev => (prev === i ? null : i));
                              }
                            }}
                            sx={{
                              cursor: d?.ticker && hasLink ? 'pointer' : 'default',
                              '&:focus-visible': { outline: `2px solid ${C.link}`, outlineOffset: '-2px' },
                              ...(isSelected && {
                                '& .MuiTableCell-body': { bgcolor: `${C.selectWash} !important` },
                                '& td:first-of-type': { boxShadow: `inset 3px 0 0 ${C.link}` },
                              }),
                            }}
                          >
                            <TableCell sx={{ width: { xs: 30, md: 40 }, textAlign: 'center', px: { xs: '4px !important', md: '12px !important' } }}>
                              {hasLink ? (
                                <IconButton aria-label={isExpanded ? 'Collapse row' : 'Expand row'} size="small" tabIndex={-1}
                                  sx={{ color: isExpanded ? C.link : C.mutedStrong, p: { xs: 0, md: 0.25 } }}>
                                  {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                </IconButton>
                              ) : null}
                            </TableCell>
                            {visibleCols.map((c) => {
                              const content = renderCell(c, d, hasLink, isExpanded, name);
                              if (content === null) return null;
                              return (
                                <TableCell
                                  key={c.field}
                                  sx={{ textAlign: isLeftCol(c.field) ? 'left' : 'right', whiteSpace: c.field === 'ticker' ? 'normal' : 'nowrap', ...(sortField === c.field && { bgcolor: 'background.sorted' }) }}
                                >
                                  {content}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                          <TableRow sx={{ '& .hist-trend svg': { bgcolor: 'transparent !important' } }}>
                            <TableCell style={{ padding: 0, border: 0 }} colSpan={colSpan}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ bgcolor: '#fafbfc', borderLeft: `3px solid ${C.link}`, borderBottom: `1px solid ${C.hair}`, p: { xs: 1, md: 2 } }}>
                                  {selHoldTicker &&
                                    <>
                                      <Box component="div" sx={{ width: '100%' }}>
                                        <Grid container spacing={2} sx={{
                                          flexWrap: { xs: 'nowrap', md: 'wrap' },
                                          overflow: { xs: 'hidden', md: 'visible' },
                                          pb: 1,
                                          '&::-webkit-scrollbar': { height: '6px' },
                                          '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '4px' },
                                        }}>

                                          {fundPortC &&
                                            <Grid size={{ xs: 11, sm: 6, md: 4 }} sx={{ minWidth: { xs: '280px', md: 'auto' } }}>
                                              <Box component="div" sx={{ mx: { xs: 0, md: 1.5 } }} className="f-h-chart section">
                                                <ChartHeader label={fundPort?.section_name?.replace('YYYYY', selHoldTicker)} value={latestVal(fundPort, true)} />
                                                <Box component="div" className="option-content hist-trend">
                                                  <StockHoldingsChart showChart={!!(selHoldTicker && tickers?.[selHoldTicker])} chart={{ ...fundPortC, id: fundPort.section_code, tick: 2 }} chartMargin={{ left: 17, bottom: 36 }} showLegend={false} showCrosshair onRendered={() => setChartInit(fundPort.section_code)} />
                                                </Box>
                                              </Box>
                                            </Grid>}

                                          {fundShrC &&
                                            <Grid size={{ xs: 11, sm: 6, md: 4 }} sx={{ minWidth: { xs: '280px', md: 'auto' } }}>
                                              <Box component="div" sx={{ mx: { xs: 0, md: 1.5 } }} className="f-h-chart section">
                                                <ChartHeader label={fundShr?.section_name?.replace('YYYYY', selHoldTicker)} value={latestVal(fundShr, false)} />
                                                <Box component="div" className="option-content hist-trend">
                                                  <StockHoldingsChart showChart={!!(selHoldTicker && tickers?.[selHoldTicker])} chart={{ ...fundShrC, id: fundShr.section_code, tick: 2 }} chartMargin={{ left: 17, bottom: 36 }} showLegend={false} showCrosshair onRendered={() => setChartInit(fundShr.section_code)} />
                                                </Box>
                                              </Box>
                                            </Grid>}

                                          {fundShrChgC &&
                                            <Grid size={{ xs: 11, sm: 6, md: 4 }} sx={{ minWidth: { xs: '280px', md: 'auto' } }}>
                                              <Box component="div" sx={{ mx: { xs: 0, md: 1.5 } }} className="f-h-chart section">
                                                <ChartHeader label={fundShrChg?.section_name?.replace('YYYYY', selHoldTicker)} value={latestVal(fundShrChg, false)} />
                                                <Box component="div" className="option-content hist-trend">
                                                  <StockHoldingsChart showChart={!!(tickers?.[selHoldTicker])} chart={{ ...fundShrChgC, id: fundShrChg.section_code, tick: 2 }} chartMargin={{ left: 17, bottom: 36 }} showLegend={false} showCrosshair onRendered={() => setChartInit(fundShrChg.section_code)} />
                                                </Box>
                                              </Box>
                                            </Grid>}

                                        </Grid>
                                      </Box>
                                    </>}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>);
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {pageCount > 1 &&
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <Pagination count={pageCount} page={safePage} onChange={handleChangePage} color="primary"
                    getItemAriaLabel={(type, p) => type === 'page' ? `Go to page ${p}` : `Go to ${type} page`}
                    sx={{ '& .MuiPaginationItem-root': { fontWeight: 500, color: C.body }, '& .MuiPaginationItem-root.Mui-selected': { color: C.hair } }} />
                </Box>}
            </Box>
          </Grid>
        </Grid>
      </Item>
    </Box>
  );
};

export default needPremium(CurrentHoldingsTable);